/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable, signal } from '@angular/core';
import { PeopleContentService } from '@alfresco/adf-content-services';
import { catchError, of, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

/**
 * The signed-in person's profile picture, shared by everything that draws it.
 *
 * Alfresco builds the "avatar" rendition after the upload returns, so a person
 * read straight afterwards still has no `avatarId`. Rather than leave the
 * picture missing until the next page load, a refresh asked for right after an
 * upload looks again a few times while the repository catches up.
 */
@Injectable({ providedIn: 'root' })
export class UserAvatarService {
  private readonly people = inject(PeopleContentService);

  /** The image URL, or empty when this person has no picture. */
  readonly url = signal('');

  private static readonly RETRY_DELAY_MS = 1200;

  /**
   * @param retries how many further looks to take while the rendition is still
   *   being made — none for an ordinary read, a few right after an upload
   * @param expectPicture whether an empty answer means "not ready yet" (after an
   *   upload) or the truth (after a removal)
   */
  refresh(retries = 0, expectPicture = true): void {
    // `getCurrentUserInfo()` answers from a cache held for the session, so it
    // would keep reporting the picture as it was when the session began
    this.people
      .getPerson('-me-')
      .pipe(catchError(() => of(null)))
      .subscribe((person: any) => {
        const avatarId = person?.avatarId;
        if (avatarId) {
          this.url.set(this.people.getUserProfileImage(avatarId));
          return;
        }

        this.url.set('');
        if (retries > 0 && expectPicture) {
          timer(UserAvatarService.RETRY_DELAY_MS)
            .pipe(switchMap(() => of(null)))
            .subscribe(() => this.refresh(retries - 1, expectPicture));
        }
      });
  }
}
